import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft,
  Handshake,
  Wallet,
  Users,
  TrendingUp,
  Gift,
  CheckCircle2,
  Loader2,
  Upload,
  X,
  Send,
  Shield,
  BadgeCheck,
  CalendarClock,
  UserCog,
  Building2,
  Crown,
  Network,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  HeadphonesIcon,
  Lock,
  PieChart,
  CreditCard,
  Bitcoin,
  Smartphone,
  Receipt,
  Mail,
  Zap,
  Ticket,
  Tag,
  ShieldAlert,
  Ban,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import { SiteHeader } from "../components/site/SiteHeader";
import { MobileBottomBar } from "../components/site/MobileBottomBar";
import { useAuth } from "../contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const AGENT_TELEGRAM =
  process.env.REACT_APP_AGENT_TELEGRAM || "https://t.me/Maxlive88_Agents_Support";
const AGENT_TELEGRAM_HANDLE = "@Maxlive88_Agents_Support";

const STATS = [];

/* ------------------------------------------------------------------
 * TypeOnce — types a multi-segment headline character-by-character on
 * mount. Each segment can have its own className, an optional line-
 * break and an inter-segment pause. Uses a smooth CSS-driven caret
 * blink for a professional, refined feel.
 * ------------------------------------------------------------------ */
function TypeOnce({ parts, speed = 90, startDelay = 500, segmentPause = 420 }) {
  const total = useMemo(
    () => parts.reduce((a, p) => a + (p.text?.length || 0), 0),
    [parts]
  );
  const [n, setN] = useState(0);
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);

  // Kick off after startDelay
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(t);
  }, [startDelay]);

  // Typing engine — with a small human-like jitter, punctuation pause,
  // and an extra pause when landing on a segment boundary (natural
  // pause after a period + line break).
  useEffect(() => {
    if (!started) return;
    if (n >= total) {
      if (!done) setDone(true);
      return;
    }
    // Detect segment boundary (end of a segment that has breakAfter).
    let atBoundary = false;
    let cursor = 0;
    for (let i = 0; i < parts.length; i++) {
      const len = parts[i].text?.length || 0;
      cursor += len;
      if (n === cursor && i < parts.length - 1 && parts[i].breakAfter) {
        atBoundary = true;
        break;
      }
    }
    // Find current character for punctuation-aware pausing.
    let currentChar = "";
    {
      let c = 0;
      for (const p of parts) {
        const len = p.text?.length || 0;
        if (n < c + len) {
          currentChar = p.text[n - c];
          break;
        }
        c += len;
      }
    }
    const isPunct = ".?!,".includes(currentChar);
    const jitter = 1 + (Math.random() - 0.5) * 0.35;
    const delay =
      Math.round(speed * jitter) +
      (isPunct ? 140 : 0) +
      (atBoundary ? segmentPause : 0);

    const t = setTimeout(() => setN((x) => x + 1), delay);
    return () => clearTimeout(t);
  }, [n, total, speed, started, parts, segmentPause, done]);

  let remaining = n;
  return (
    <span className="tw-fade-in inline">
      {parts.map((p, idx) => {
        const len = p.text?.length || 0;
        const show = Math.max(0, Math.min(len, remaining));
        remaining -= len;
        return (
          <span key={idx}>
            <span className={p.className}>{p.text.substring(0, show)}</span>
            {p.breakAfter && show === len ? (
              <br className="hidden sm:block" />
            ) : null}
          </span>
        );
      })}
      <span
        aria-hidden
        className={`tw-caret ${done ? "tw-caret-done" : ""}`}
      />
      <style>{`
        .tw-fade-in {
          opacity: 0;
          animation: tw-fade-in-kf 700ms ease-out forwards;
        }
        @keyframes tw-fade-in-kf {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .tw-caret {
          display: inline-block;
          margin-left: 6px;
          width: 3px;
          height: 0.82em;
          vertical-align: -0.05em;
          border-radius: 2px;
          background: linear-gradient(180deg, #6ee7b7 0%, #10b981 100%);
          box-shadow: 0 0 12px rgba(16,185,129,0.75), 0 0 24px rgba(16,185,129,0.35);
          animation: tw-blink 1.05s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .tw-caret-done {
          animation: tw-blink-slow 1.6s ease-in-out infinite;
        }
        @keyframes tw-blink {
          0%, 45%   { opacity: 1; }
          50%, 95%  { opacity: 0; }
          100%      { opacity: 1; }
        }
        @keyframes tw-blink-slow {
          0%, 50%   { opacity: 1; }
          55%, 95%  { opacity: 0.15; }
          100%      { opacity: 1; }
        }
      `}</style>
    </span>
  );
}

/* ------------------------------------------------------------------
 * useInView — returns [ref, inView] using IntersectionObserver.
 * Fires once (unobserves after first intersection) to keep animations
 * from re-triggering on re-scroll.
 * ------------------------------------------------------------------ */
function useInView({ threshold = 0.35, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, inView]);
  return [ref, inView];
}

/* ------------------------------------------------------------------
 * TypeInView — types a single string character-by-character once the
 * element scrolls into view. Includes an emerald blinking caret that
 * fades out after typing completes. Used for section titles.
 * ------------------------------------------------------------------ */
function TypeInView({ text = "", speed = 55, startDelay = 120, className = "" }) {
  const [wrapRef, inView] = useInView();
  const [n, setN] = useState(0);
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  const [caretVisible, setCaretVisible] = useState(true);

  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(t);
  }, [inView, startDelay]);

  useEffect(() => {
    if (!started) return;
    if (n >= text.length) {
      if (!done) setDone(true);
      // fade caret out ~1s after done
      const f = setTimeout(() => setCaretVisible(false), 1100);
      return () => clearTimeout(f);
    }
    const ch = text[n];
    const isPunct = ".?!,।".includes(ch);
    const jitter = 1 + (Math.random() - 0.5) * 0.3;
    const delay = Math.round(speed * jitter) + (isPunct ? 120 : 0);
    const t = setTimeout(() => setN((x) => x + 1), delay);
    return () => clearTimeout(t);
  }, [n, text, speed, started, done]);

  return (
    <span ref={wrapRef} className={className}>
      <span>{text.substring(0, n)}</span>
      {caretVisible && (
        <span
          aria-hidden
          className={`tw-caret-sm ${done ? "tw-caret-sm-fade" : ""}`}
        />
      )}
      <style>{`
        .tw-caret-sm {
          display: inline-block;
          margin-left: 5px;
          width: 2px;
          height: 0.78em;
          vertical-align: -0.02em;
          border-radius: 1px;
          background: linear-gradient(180deg, #6ee7b7 0%, #10b981 100%);
          box-shadow: 0 0 8px rgba(16,185,129,0.7);
          animation: tw-blink-sm 1.05s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes tw-blink-sm {
          0%, 45%  { opacity: 1; }
          50%, 95% { opacity: 0; }
          100%     { opacity: 1; }
        }
        .tw-caret-sm-fade {
          animation: tw-caret-sm-fade-kf 1200ms ease-out forwards;
        }
        @keyframes tw-caret-sm-fade-kf {
          0%   { opacity: 1; }
          70%  { opacity: 0.4; }
          100% { opacity: 0; }
        }
      `}</style>
    </span>
  );
}

/* ------------------------------------------------------------------
 * CountUpInView — smoothly counts a number up from 0 to `end` once
 * the element scrolls into view. Used for commission percentages and
 * the "১৫ দিন" cell. Supports Bangla digits.
 * ------------------------------------------------------------------ */
const BN_DIGITS = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
function toBanglaDigits(str) {
  return String(str).replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)]);
}
function CountUpInView({
  end,
  suffix = "",
  duration = 1400,
  className = "",
  bangla = false,
  prefix = "",
}) {
  const [ref, inView] = useInView({ threshold: 0.4 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const startTs = performance.now();
    const from = 0;
    const to = Number(end) || 0;
    const step = (ts) => {
      const t = Math.min(1, (ts - startTs) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);
  const display = Math.round(val);
  const shown = bangla ? toBanglaDigits(display) : String(display);
  return (
    <span ref={ref} className={className}>
      {prefix}
      {shown}
      {suffix}
    </span>
  );
}

/* ------------------------------------------------------------------
 * AnimatedBenefitValue — maps a Bangla-formatted commission-card value
 * ("৬%", "৪%", "১৫ দিন") to a smooth count-up animation.
 * ------------------------------------------------------------------ */
function AnimatedBenefitValue({ value }) {
  // Match patterns like "৬%", "১৫%", "১৫ দিন", etc.
  const m = String(value).match(/^([০-৯0-9]+)(.*)$/);
  if (!m) return <>{value}</>;
  const num = m[1].replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)));
  const rest = m[2] || "";
  return (
    <CountUpInView
      end={Number(num)}
      suffix={rest}
      bangla={true}
      duration={1400}
    />
  );
}


/* ------------------------------------------------------------------
 * Typewriter — professional rotating headline used in the hero.
 * Types each phrase in, holds, deletes, then moves to the next.
 * Includes a blinking caret that mimics a real terminal cursor.
 * ------------------------------------------------------------------ */
function Typewriter({
  phrases,
  typingSpeed = 85,
  deletingSpeed = 40,
  pauseMs = 2200,
  className = "",
}) {
  const [idx, setIdx] = useState(0);
  const [sub, setSub] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [blink, setBlink] = useState(true);

  // caret blink
  useEffect(() => {
    const t = setInterval(() => setBlink((b) => !b), 500);
    return () => clearInterval(t);
  }, []);

  // typing engine
  useEffect(() => {
    if (!phrases || phrases.length === 0) return;
    const current = phrases[idx % phrases.length];

    if (!deleting && sub === current.length) {
      const t = setTimeout(() => setDeleting(true), pauseMs);
      return () => clearTimeout(t);
    }
    if (deleting && sub === 0) {
      setDeleting(false);
      setIdx((i) => (i + 1) % phrases.length);
      return;
    }
    const t = setTimeout(
      () => setSub((s) => s + (deleting ? -1 : 1)),
      deleting ? deletingSpeed : typingSpeed
    );
    return () => clearTimeout(t);
  }, [sub, deleting, idx, phrases, typingSpeed, deletingSpeed, pauseMs]);

  const text = phrases[idx % phrases.length].substring(0, sub);

  return (
    <span className={className} data-testid="become-agent-typewriter" aria-live="polite">
      <span>{text}</span>
      <span
        className={`inline-block ml-0.5 w-[3px] h-[1em] align-[-0.15em] bg-emerald-400 rounded-[1px] shadow-[0_0_8px_rgba(16,185,129,0.7)] ${
          blink ? "opacity-100" : "opacity-0"
        } transition-opacity duration-100`}
        aria-hidden="true"
      />
    </span>
  );
}

const AGENT_BENEFITS = [
  { icon: Wallet, label: "ডিপোজিটে কমিশন", value: "৬%", hint: "প্রতি মাসের deposit turnover এর উপর।", note: "কমিশন আলোচনা সাপেক্ষে কম-বেশি করা যেতে পারে।", tone: "emerald" },
  { icon: TrendingUp, label: "উইথড্রতে কমিশন", value: "৪%", hint: "প্রতি মাসের withdraw turnover এর উপর।", note: "কমিশন আলোচনা সাপেক্ষে কম-বেশি করা যেতে পারে।", tone: "emerald" },
  { icon: CalendarClock, label: "কমিশন পেমেন্ট", value: "১৫ দিন", hint: "প্রতি ১৫ দিন পর পর automatic settle।", tone: "amber" },
];

const CUSTOMER_BENEFITS = [
  { icon: Wallet, title: "ইনস্ট্যান্ট ৩% বোনাস", desc: "ডিপোজিটের সাথে সাথে ৩% বোনাস সরাসরি player ID তে।" },
  { icon: Gift, title: "প্রতিদিন ফ্রী স্পিন", desc: "প্রতিটি কাস্টমার প্রতিদিন ১টি করে ফ্রী স্পিন পাবেন।" },
];

const REQUIREMENTS = [
  "এন আইডি কার্ডের দুই পাশের ভালো করে ছবি",
  "হোল্ডিং ফটো (NID হাতে নিয়ে নিজের ছবি)",
  "নাম, মোবাইল নাম্বার, ই-মেইল",
  "সম্পূর্ণ ঠিকানা — City ও State address সহ",
  "ইউজার নিক নেম",
  "৳13,000 Initial Deposit — সরাসরি আপনার Agent Balance এ credit হবে (Crypto / e-Wallet)",
];

const MASTER_PERKS = [
  "নিজের অধীনে unlimited Sub-Agent নিয়োগ",
  "Sub-Agent এর লেনদেন থেকে override commission (নিচে দেখুন)",
  "Agent Panel এ Self-serve balance load — Crypto / bKash / Nagad / Rocket",
  "Dedicated account manager + priority support",
  "Custom promo code + marketing kit",
];
const SUB_PERKS = [
  "Master Agent এর guidance ও training",
  "একই ৬% / ৪% commission structure",
  "Agent Panel এ Self-serve balance load — Crypto / bKash / Nagad / Rocket",
  "১৫ দিনে দ্রুত settlement",
  "Live dashboard — clicks, deposits, NGR",
];

const FAQS = [
  { q: "এজেন্ট হতে কতদিন লাগে?", a: "Payment successful হওয়ার পর সব document সঠিক থাকলে সাধারণত ২৪ ঘন্টার মধ্যে account activate করা হয়। Complex case এ ৪৮ ঘন্টা লাগতে পারে।" },
  { q: "Sub-Agent দেওয়া যায় কি? Commission কত?", a: "হ্যাঁ। Master Agent হিসেবে approve হলে unlimited Sub-Agent যুক্ত করতে পারবেন। প্রতিটি Sub-Agent এর Deposit ১% এবং Withdraw ১% override commission — ১৫ দিন পর পর payout, আপনার নিজের ৬% / ৪% এর অতিরিক্ত।" },
  { q: "৳13,000 টাকা কি fee? এই টাকা কি হারিয়ে যাবে?", a: "না, এটি কোনো fee নয়। এটি আপনার প্রথম Agent Balance Deposit — সম্পূর্ণ ৳13,000 আপনার Agent Balance এ credit হবে এবং এই balance থেকে কাস্টমারদের deposit process করবেন। অর্থাৎ এই টাকা আপনার নিজের operating balance।" },
  { q: "কোন কোন payment gateway support করে?", a: "Crypto (USDT / TRX / BNB / ETH / TON / USDC) এবং e-Wallet (bKash / Nagad / Rocket) — সাইটে configure করা যেকোনো enabled gateway দিয়ে pay করা যাবে।" },
  { q: "Agent হওয়ার পর balance কীভাবে load করব?", a: "Agent Panel এ ঢুকে নিজেই self-serve balance load করতে পারবেন — Crypto (USDT / TRX / BNB ইত্যাদি) অথবা e-Wallet (bKash / Nagad / Rocket) — যেকোনো gateway দিয়ে। Admin এর জন্য অপেক্ষা করতে হবে না, payment confirm হওয়ার সাথে সাথে balance instant credit হয়ে যাবে।" },
  { q: "Payment successful হলে কী পাবো?", a: "আপনার email এ instant deposit receipt (PDF attachment সহ) পাঠানো হবে। Approve বা reject হলেও email notification পাবেন।" },
  { q: "কী কারণে Agent account close করা হতে পারে?", a: "Fake / self-generated transaction করলে, কাস্টমারদের সাথে scam হলে, অথবা approve হওয়ার পর ৩০ দিন কোনো লেনদেন না হলে — এই সব ক্ষেত্রেই Agent account চিরদিনের জন্য বন্ধ করে দেওয়া হবে (নিচের Policy section দেখুন)।" },
];

const CATEGORY_META = {
  bkash: { icon: Smartphone, label: "bKash", accent: "#E2136E" },
  nagad: { icon: Smartphone, label: "Nagad", accent: "#EC5B24" },
  rocket: { icon: Smartphone, label: "Rocket", accent: "#8B2E86" },
  upay:  { icon: Smartphone, label: "Upay", accent: "#F58120" },
  usdt_trc20: { icon: Bitcoin, label: "USDT (TRC-20)", accent: "#26A17B" },
  usdt_bep20: { icon: Bitcoin, label: "USDT (BEP-20)", accent: "#F3BA2F" },
  trx: { icon: Bitcoin, label: "TRX", accent: "#EF0027" },
  bnb: { icon: Bitcoin, label: "BNB", accent: "#F3BA2F" },
  eth: { icon: Bitcoin, label: "ETH", accent: "#627EEA" },
  ton: { icon: Bitcoin, label: "TON", accent: "#0098EA" },
  usdc_polygon: { icon: Bitcoin, label: "USDC (Polygon)", accent: "#2775CA" },
};

/* ═════════════════════════ MAIN COMPONENT ═════════════════════════ */

export default function BecomeAgentPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);      // { fee_bdt, gateways }
  const [submitting, setSubmitting] = useState(false);
  const [uploads, setUploads] = useState({ nid_front: null, nid_back: null, holding_photo: null });
  const [uploading, setUploading] = useState({ nid_front: false, nid_back: false, holding_photo: false });
  const [selection, setSelection] = useState({ gateway_id: "", gateway_method: "" });
  const [coupon, setCoupon] = useState({ code: "", applying: false, applied: null }); // applied = validated response
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "", state: "",
    username: "", telegram: "", whatsapp: "", message: "", payer_account: "",
  });

  useEffect(() => {
    axios.get(`${BACKEND}/api/site/agent-apply/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.full_name || user.username || "",
        phone: f.phone || user.phone || "",
        email: f.email || user.email || "",
        username: f.username || user.username || "",
      }));
    }
  }, [user]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  /* ── Wizard scroll: on step change, bring the FORM card to the top of the
     viewport (not the page hero) so the user lands right on the next step. ── */
  const wizardRef = useRef(null);
  const scrollToWizard = () => {
    requestAnimationFrame(() => {
      const el = wizardRef.current;
      if (!el) return window.scrollTo({ top: 0, behavior: "smooth" });
      const y = el.getBoundingClientRect().top + window.pageYOffset - 12;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    });
  };

  /* ── Upload handlers ── */
  const handleUpload = async (key, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be 8 MB or smaller");
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${BACKEND}/api/site/agent-apply/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const preview = URL.createObjectURL(file);
      setUploads((u) => ({ ...u, [key]: { file_id: data.file_id, url: data.url, preview } }));
      toast.success("Uploaded");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };
  const removeUpload = (key) => setUploads((u) => ({ ...u, [key]: null }));

  /* ── Step navigation ── */
  const validateStep1 = () => {
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("সঠিক নাম দিন");
    if (!form.phone.trim() || form.phone.trim().length < 6) return toast.error("সঠিক মোবাইল নাম্বার দিন");
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("সঠিক ই-মেইল দিন");
    if (!form.address.trim()) return toast.error("ঠিকানা দিন");
    if (!form.city.trim()) return toast.error("City দিন");
    setStep(2);
    scrollToWizard();
  };
  const validateStep2 = () => {
    if (!uploads.nid_front) return toast.error("NID সামনের ছবি upload করুন");
    if (!uploads.nid_back) return toast.error("NID পিছনের ছবি upload করুন");
    if (!uploads.holding_photo) return toast.error("হোল্ডিং ফটো upload করুন");
    setStep(3);
    scrollToWizard();
  };
  const back = () => { setStep((s) => Math.max(1, s - 1)); scrollToWizard(); };

  /* ── Final submit → get redirect_url ── */
  const doSubmit = async () => {
    if (!selection.gateway_id || !selection.gateway_method) {
      return toast.error("Payment method select করুন");
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        gateway_id: selection.gateway_id,
        gateway_method: selection.gateway_method,
        nid_front_file_id: uploads.nid_front?.file_id || "",
        nid_back_file_id: uploads.nid_back?.file_id || "",
        holding_photo_file_id: uploads.holding_photo?.file_id || "",
        coupon_code: coupon.applied?.code || "",
      };
      const { data } = await axios.post(`${BACKEND}/api/site/agent-applications`, payload, { withCredentials: true });
      toast.success("Redirecting to secure payment...");
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        navigate(`/become-agent/return?app=${data.application_id}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Application submit করা যায়নি";
      toast.error(typeof msg === "string" ? msg : "Submit failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080d13] text-white pb-24 lg:pb-20" data-testid="become-agent-page">
      <div className="hidden lg:block"><SiteHeader /></div>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-[#080d13]/95 backdrop-blur border-b border-white/5">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            onClick={() => (step > 1 ? back() : navigate(-1))}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/5"
            data-testid="become-agent-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-extrabold tracking-tight">Become an Agent</h1>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.15),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }} />
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-emerald-400/5 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 pt-8 pb-6 lg:pt-20 lg:pb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 pl-2 pr-4 py-1">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold px-2 py-0.5">
              <Sparkles className="w-3 h-3" /> NEW
            </span>
            <span className="text-[11px] font-semibold text-emerald-300 tracking-wide">MAXLIVE88 OFFICIAL AGENT PROGRAM</span>
          </div>
          <h1
            className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mt-5 max-w-4xl min-h-[4rem] sm:min-h-[6.4rem] lg:min-h-[8rem]"
            data-testid="become-agent-hero-h1"
          >
            <span className="sr-only">Become an Agent. Build a network. Earn every 15 days.</span>
            <span aria-hidden="true">
              <TypeOnce
                speed={90}
                startDelay={500}
                segmentPause={520}
                parts={[
                  { text: "Become an Agent.", className: "text-white", breakAfter: true },
                  {
                    text: "Build a network. Earn every 15 days.",
                    className:
                      "bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 bg-clip-text text-transparent",
                  },
                ]}
              />
            </span>
          </h1>
          <div
            className="mt-5 flex items-start gap-2.5 min-h-[2em] max-w-3xl"
            data-testid="become-agent-typewriter-wrap"
          >
            <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 mt-1 text-emerald-400 shrink-0 animate-pulse" />
            <Typewriter
              phrases={[
                "Build your own network of players.",
                "Earn 6% deposit + 4% withdraw commission.",
                "Auto payout every 15 days — no manual follow-up.",
                "নিজের team, নিজের income — every single day.",
                "Master Agent হয়ে unlimited Sub-Agent নিয়োগ করুন।",
                "Dedicated account manager. Priority support 24/7.",
              ]}
              className="text-sm sm:text-base lg:text-lg font-semibold tracking-tight text-emerald-200/95 whitespace-normal break-words"
            />
          </div>
          <p className="text-white/70 text-sm sm:text-base lg:text-lg mt-4 sm:mt-5 max-w-2xl leading-relaxed">
            <span className="text-white font-semibold">Maxlive88</span> এ Master Agent বা Sub-Agent
            হয়ে কাজ করুন। প্রথমবার এজেন্ট হতে <span className="text-white font-semibold">৳13,000</span>{" "}
            deposit করুন — সম্পূর্ণ টাকা আপনার <span className="text-emerald-300 font-semibold">Agent Balance</span> এ credit হবে।
            Crypto অথবা e-Wallet, যেকোনো gateway দিয়ে instant।
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm shadow-[0_8px_30px_rgba(16,185,129,0.35)] transition-all hover:-translate-y-0.5"
              data-testid="become-agent-cta"
            >
              Start application <ArrowRight className="w-4 h-4" />
            </a>
            <a href={AGENT_TELEGRAM} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#229ED9]/15 hover:bg-[#229ED9]/25 text-[#5FB8E6] border border-[#229ED9]/40 font-semibold text-sm"
               data-testid="become-agent-telegram-hero">
              <Send className="w-4 h-4" /> Talk to Admin (Telegram)
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
        {/* Benefits */}
        <SectionHeading kicker="COMMISSION STRUCTURE" title="Maxlive88 এজেন্টদের সুবিধাসমূহ" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {AGENT_BENEFITS.map(({ icon: Icon, label, value, hint, note, tone }) => (
            <div key={label} className={`relative overflow-hidden rounded-2xl border p-6 transition-all hover:-translate-y-0.5 ${
              tone === "emerald" ? "border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02]"
                                 : "border-amber-500/25 bg-gradient-to-br from-amber-500/10 to-amber-500/[0.02]"
            }`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border mb-4 ${
                tone === "emerald" ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
                                   : "bg-amber-500/15 border-amber-500/30 text-amber-400"
              }`}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">{label}</div>
              <div className={`text-4xl font-black mt-1 ${tone === "emerald" ? "text-emerald-400" : "text-amber-400"}`}>
                <AnimatedBenefitValue value={value} />
              </div>
              <div className="text-sm text-white/60 mt-2 leading-relaxed">{hint}</div>
              {note && (
                <div className="mt-3 inline-flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200/90 text-[11px] leading-snug px-2.5 py-1.5">
                  <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-amber-300" />
                  <span>{note}</span>
                </div>
              )}
              <div className={`absolute -bottom-8 -right-8 w-32 h-32 rounded-full blur-2xl ${tone === "emerald" ? "bg-emerald-500/10" : "bg-amber-500/10"}`} />
            </div>
          ))}
        </div>

        {/* Hierarchy */}
        <SectionHeading kicker="TEAM STRUCTURE" title="Master Agent → Sub Agent Network"
                        subtitle="Solo হয়ে কাজ করুন, অথবা নিজের team নিয়ে একটি pyramid গড়ে তুলুন।" />
        <div className="rounded-3xl border border-white/5 bg-gradient-to-b from-[#0f1620] to-[#0a1119] p-6 lg:p-10 overflow-hidden">
          <HierarchyDiagram />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            <RolePerkCard icon={Crown} tone="amber" title="Master Agent" tag="RECOMMENDED" perks={MASTER_PERKS} />
            <RolePerkCard icon={Network} tone="emerald" title="Sub Agent" tag="EASY START" perks={SUB_PERKS} />
          </div>
          {/* Override commission block */}
          <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] via-emerald-500/[0.03] to-transparent p-5 lg:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">MASTER AGENT BONUS</div>
                <div className="text-base lg:text-lg font-black text-white">Sub-Agent এর লেনদেন থেকে Override Commission</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <BonusCell icon={Wallet} label="Deposit" value="১%" hint="Sub-Agent এর deposit এর উপর" />
              <BonusCell icon={TrendingUp} label="Withdraw" value="১%" hint="Sub-Agent এর withdraw এর উপর" />
              <BonusCell icon={CalendarClock} label="Payout" value="১৫ দিন" tone="amber" hint="Automatic settle" />
            </div>
          </div>

          {/* Self-serve balance loading block */}
          <div className="mt-6 rounded-2xl border border-[#229ED9]/30 bg-gradient-to-br from-[#229ED9]/[0.08] via-[#229ED9]/[0.03] to-transparent p-5 lg:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#229ED9]/15 border border-[#229ED9]/30 flex items-center justify-center text-[#5FB8E6]">
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#5FB8E6] font-bold">AGENT PANEL FEATURE</div>
                <div className="text-base lg:text-lg font-black text-white">
                  নিজে নিজে Agent Balance Load করুন — যেকোনো সময়, যেকোনো method এ
                </div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              এজেন্ট হিসেবে approve হওয়ার পর <span className="text-white font-semibold">Agent Panel</span> এ ঢুকে
              আপনি নিজে নিজেই balance load করতে পারবেন — <span className="text-[#5FB8E6] font-semibold">Admin এর জন্য অপেক্ষা করতে হবে না</span>।
              Crypto বা e-Wallet (bKash / Nagad / Rocket) — সাইটে configured যেকোনো gateway দিয়ে instant top-up।
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <BalanceMethodCell icon={Bitcoin} label="Crypto" tone="amber" hint="USDT / TRX / BNB / ETH / TON" />
              <BalanceMethodCell icon={Smartphone} label="bKash" tone="pink" hint="Instant top-up" />
              <BalanceMethodCell icon={Smartphone} label="Nagad" tone="orange" hint="Instant top-up" />
              <BalanceMethodCell icon={Smartphone} label="Rocket" tone="purple" hint="Instant top-up" />
            </div>
            <div className="mt-4 flex items-start gap-2 text-xs text-white/60">
              <Lock className="w-3.5 h-3.5 mt-0.5 text-[#5FB8E6] shrink-0" />
              <span>
                সব transaction PCI-secure gateway দিয়ে process হয় — payment confirmation এর সাথে সাথে
                Agent Panel এ balance credit হয়ে যায়।
              </span>
            </div>
          </div>
        </div>

        {/* Customer benefits */}
        <SectionHeading kicker="PLAYER PERKS" title="কাস্টমারদের সুবিধা" />
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {CUSTOMER_BENEFITS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-white/5 bg-[#0f1620] p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-4 hover:border-emerald-500/25 transition-colors">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
              </div>
              <div>
                <div className="font-bold text-white text-sm sm:text-lg leading-snug">{title}</div>
                <div className="text-xs sm:text-sm text-white/60 mt-1 leading-relaxed">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Requirements + fee */}
        <SectionHeading kicker="ELIGIBILITY" title="এজেন্ট হতে যা যা লাগবে" />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 rounded-2xl border border-white/5 bg-[#0f1620] p-6 lg:p-7">
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-1 sm:gap-0 sm:space-y-3">
              {REQUIREMENTS.map((r, i) => (
                <li key={r} className="flex items-start gap-2.5 sm:gap-3">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0 text-emerald-400 text-[11px] sm:text-xs font-bold">
                    {i + 1}
                  </div>
                  <span className="text-[13px] sm:text-base text-white/85 leading-relaxed pt-0.5">{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] p-6 lg:p-7">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-4">
              <Receipt className="w-6 h-6" />
            </div>
            <div className="text-xs uppercase tracking-widest text-emerald-300/80 font-semibold">Initial Agent Balance Deposit</div>
            <div className="text-4xl font-black text-emerald-400 mt-1">৳13,000</div>
            <p className="text-sm text-white/70 mt-3 leading-relaxed">
              <span className="font-semibold text-white">এটি কোনো fee নয়</span> — সম্পূর্ণ ৳13,000
              সরাসরি আপনার <span className="text-emerald-300 font-semibold">Agent Balance</span> এ credit হবে।
              এই balance থেকেই আপনি কাস্টমারদের deposit process করবেন। Crypto বা e-Wallet — যেকোনো gateway দিয়ে instant।
            </p>
          </div>
        </div>

        {/* Telegram card */}
        <div className="mt-8 relative overflow-hidden rounded-3xl border border-[#229ED9]/30 bg-gradient-to-r from-[#229ED9]/15 via-[#229ED9]/5 to-transparent p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#229ED9]/20 blur-3xl" />
          <div className="relative w-14 h-14 rounded-2xl bg-[#229ED9]/20 border border-[#229ED9]/40 flex items-center justify-center text-[#5FB8E6] shrink-0">
            <Send className="w-7 h-7" />
          </div>
          <div className="relative flex-1">
            <div className="text-xs uppercase tracking-widest text-[#5FB8E6] font-semibold">AGENT SUPPORT</div>
            <div className="text-xl lg:text-2xl font-bold text-white mt-1">সরাসরি Admin এর সাথে Telegram এ কথা বলুন</div>
            <div className="text-sm text-white/60 mt-1">যেকোনো প্রশ্ন, application status, বা partnership discussion — instant reply, 24/7।</div>
            <div className="mt-2 inline-flex items-center gap-2 text-sm font-mono text-[#5FB8E6] bg-[#229ED9]/10 border border-[#229ED9]/30 rounded-full px-3 py-1">
              <Send className="w-3.5 h-3.5" /> {AGENT_TELEGRAM_HANDLE}
            </div>
          </div>
          <a href={AGENT_TELEGRAM} target="_blank" rel="noreferrer"
             className="relative inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#229ED9] hover:bg-[#1E8ABF] text-white font-bold text-sm shadow-lg shadow-[#229ED9]/30"
             data-testid="become-agent-telegram">
            <Send className="w-4 h-4" /> Open Telegram
          </a>
        </div>

        {/* FAQ */}
        <SectionHeading kicker="POLICIES · IMPORTANT" title="Agent Policy & Terms"
                        subtitle="Please read carefully — এইসব ক্ষেত্রে Agent account চিরদিনের জন্য বন্ধ হয়ে যাবে।" />
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:gap-4">
          <PolicyCard
            icon={ShieldAlert}
            title="Fake / self-generated লেনদেন নিষিদ্ধ"
            body={<>Agent নিজে নিজে fake লেনদেন তৈরি করে commission generate করার চেষ্টা করলে <span className="font-bold text-red-300">Agent account চিরদিনের জন্য বন্ধ</span> করে দেওয়া হবে — কোনো second chance নেই।</>}
            tone="red"
          />
          <PolicyCard
            icon={AlertTriangle}
            title="কাস্টমার scam করলে কঠোর ব্যবস্থা"
            body={<>কাস্টমারের সাথে scam / প্রতারণা করলে — টাকা আত্মসাৎ, ভুল information দেওয়া বা KYC data অপব্যবহার — সঙ্গে সঙ্গে <span className="font-bold text-red-300">account permanent ban</span> এবং প্রয়োজনে আইনি ব্যবস্থা নেওয়া হবে।</>}
            tone="red"
          />
          <PolicyCard
            icon={Ban}
            title="৩০ দিন Inactive → Account Close"
            body={<>Agent হিসেবে approve হওয়ার পর একটানা <span className="font-bold text-amber-300">৩০ দিন কোনো লেনদেন না করলে</span> account inactivity policy অনুযায়ী চিরদিনের জন্য close হয়ে যাবে। Deposit refundable নয়।</>}
            tone="amber"
          />
          <PolicyCard
            icon={Shield}
            title="Compliance & Fair Play"
            body={<>Multi-account, referral abuse, bonus abuse বা অন্য কোনো unfair practice detect হলে Agent account সাথে সাথে suspend করে investigation চালানো হবে। প্রমাণিত হলে <span className="font-bold text-red-300">permanent close</span>।</>}
            tone="red"
          />
        </div>

        {/* FAQ */}
        <SectionHeading kicker="FAQ" title="প্রশ্ন ও উত্তর" />
        <div className="rounded-2xl border border-white/5 bg-[#0f1620] px-5 lg:px-7">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-white/5 last:border-b-0">
                <AccordionTrigger className="text-left text-white hover:no-underline py-5" data-testid={`faq-${i}`}>
                  <span className="pr-4 font-semibold">{f.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-white/70 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* ═════════════ APPLICATION WIZARD ═════════════ */}
        <div ref={wizardRef} id="apply" className="mt-10 lg:mt-20 relative overflow-hidden rounded-2xl lg:rounded-3xl border border-emerald-500/25 bg-[#0b1218] p-0 shadow-[0_30px_80px_-30px_rgba(16,185,129,0.35)]">
          {/* Decorative glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-500/10 blur-[100px]" />
          <div className="pointer-events-none absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />

          {/* ── Premium Header ── */}
          <div className="relative border-b border-white/5 bg-gradient-to-b from-emerald-500/[0.06] to-transparent px-4 sm:px-6 lg:px-10 pt-6 lg:pt-9 pb-6 lg:pb-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Left — title block */}
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-xl bg-emerald-500/12 border border-emerald-500/30 flex items-center justify-center shrink-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                  <BadgeCheck className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold">
                    <Lock className="w-3 h-3" /> Secure Agent Application
                  </div>
                  <h3 className="text-[22px] leading-tight sm:text-2xl lg:text-[30px] lg:leading-[1.15] font-black mt-1.5 text-white">
                    Complete your application in <span className="text-emerald-400">3 simple steps</span>
                  </h3>
                  <p className="text-[13px] lg:text-sm text-white/60 mt-2 max-w-2xl leading-relaxed">
                    Personal Info <span className="text-white/30">›</span> Documents <span className="text-white/30">›</span> Payment.
                    আপনার ৳13,000 সম্পূর্ণটাই <span className="text-emerald-300 font-semibold">Agent Balance</span> এ credit হবে —
                    এটি কোনো fee নয়। Payment successful হলে আপনার email-এ PDF receipt পৌঁছাবে,
                    এবং ২৪ ঘন্টার মধ্যে approval notification আসবে।
                  </p>
                </div>
              </div>

              {/* Right — trust chips */}
              <div className="grid grid-cols-2 lg:flex lg:flex-col gap-1.5 lg:gap-2 lg:min-w-[220px] shrink-0">
                <HeaderTrustPill icon={Lock} text="256-bit SSL Encrypted" />
                <HeaderTrustPill icon={Receipt} text="Instant PDF Receipt" />
                <HeaderTrustPill icon={CalendarClock} text="24-hour Review" />
                <HeaderTrustPill icon={Shield} text="100% Refund if Rejected" />
              </div>
            </div>
          </div>

          {/* ── Progress Stepper ── */}
          <div className="relative px-4 sm:px-6 lg:px-10 pt-6 lg:pt-8">
            <div className="relative">
              {/* progress rail */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-white/5 rounded-full hidden sm:block" />
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full hidden sm:block transition-all duration-500"
                style={{ width: `${((step - 1) / 2) * 100}%` }}
              />
              <div className="relative grid grid-cols-3 gap-2 lg:gap-3" data-testid="wizard-stepper">
                <StepPill n={1} title="Personal & Address" short="Personal" current={step} icon={UserCog} />
                <StepPill n={2} title="Documents" short="Documents" current={step} icon={Shield} />
                <StepPill n={3} title="Payment" short="Payment" current={step} icon={CreditCard} />
              </div>
            </div>
          </div>

          {/* ── Wizard body ── */}
          <div className="relative px-4 sm:px-6 lg:px-10 pt-6 lg:pt-8 pb-6 lg:pb-10">

            {/* STEP 1 */}
            {step === 1 && (
              <div className="space-y-7 animate-in fade-in duration-200" data-testid="step-personal">
                {/* Contact Information */}
                <FormSection
                  index="01"
                  title="Personal Information"
                  subtitle="আপনার সঠিক তথ্য দিন — approval এর সময় verify করা হবে।"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="নাম *" hint="Full name" value={form.name} onChange={onChange("name")} testId="ba-name" placeholder="আপনার পুরো নাম" />
                    <Field label="মোবাইল নাম্বার *" hint="Mobile number" value={form.phone} onChange={onChange("phone")} testId="ba-phone" placeholder="01XXXXXXXXX" />
                    <Field label="ই-মেইল *" hint="Receipt & notifications এই email-এ যাবে" value={form.email} onChange={onChange("email")} testId="ba-email" placeholder="you@example.com" type="email" />
                    <Field label="ইউজার নিক নেম" hint="Maxlive88 username (optional)" value={form.username} onChange={onChange("username")} testId="ba-username" placeholder="player123" />
                  </div>
                </FormSection>

                {/* Address */}
                <FormSection
                  index="02"
                  title="Residential Address"
                  subtitle="NID অনুযায়ী current ঠিকানা দিন।"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Field label="ঠিকানা *" hint="Street address" value={form.address} onChange={onChange("address")} testId="ba-address" placeholder="বাড়ি, রোড, এলাকা" />
                    </div>
                    <Field label="City *" hint="শহর" value={form.city} onChange={onChange("city")} testId="ba-city" placeholder="Dhaka" />
                    <Field label="State / Division" hint="বিভাগ" value={form.state} onChange={onChange("state")} testId="ba-state" placeholder="Division / State" />
                  </div>
                </FormSection>

                {/* Social Handles */}
                <FormSection
                  index="03"
                  title="Contact Handles"
                  subtitle="দ্রুত communication এর জন্য (optional)।"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Telegram username" hint="@handle" value={form.telegram} onChange={onChange("telegram")} testId="ba-telegram" placeholder="@yourhandle" />
                    <Field label="WhatsApp number" hint="Country code সহ" value={form.whatsapp} onChange={onChange("whatsapp")} testId="ba-whatsapp" placeholder="+880 1XXXXXXXXX" />
                  </div>
                </FormSection>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
                  <div className="text-[11px] text-white/40 flex items-center gap-1.5">
                    <Lock className="w-3 h-3 text-emerald-400/70" />
                    আপনার তথ্য encrypted transit-এ পাঠানো হয় ও strictly confidential রাখা হয়।
                  </div>
                  <button onClick={validateStep1}
                          className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)]"
                          data-testid="ba-next-1">
                    Continue to Documents <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="space-y-6 animate-in fade-in duration-200" data-testid="step-documents">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <UploadTile label="NID — সামনের ছবি" value={uploads.nid_front} loading={uploading.nid_front}
                              onFile={(f) => handleUpload("nid_front", f)} onRemove={() => removeUpload("nid_front")} testId="ba-nid-front" />
                  <UploadTile label="NID — পিছনের ছবি" value={uploads.nid_back} loading={uploading.nid_back}
                              onFile={(f) => handleUpload("nid_back", f)} onRemove={() => removeUpload("nid_back")} testId="ba-nid-back" />
                  <UploadTile label="হোল্ডিং ফটো" value={uploads.holding_photo} loading={uploading.holding_photo}
                              onFile={(f) => handleUpload("holding_photo", f)} onRemove={() => removeUpload("holding_photo")} testId="ba-holding" />
                </div>
                <p className="text-[11px] text-white/40">JPG / PNG, max 8 MB প্রতিটি। NID clear ও পুরো দৃশ্যমান হতে হবে।</p>
                <div>
                  <label className="text-xs font-medium text-white/70">আপনার audience সম্পর্কে জানান (optional)</label>
                  <textarea value={form.message} onChange={onChange("message")} rows={3}
                            placeholder="Facebook group, Telegram channel, YouTube, personal network..."
                            className="mt-1.5 w-full rounded-xl bg-[#0a1119] border border-white/10 focus:border-emerald-500/50 outline-none px-4 py-3 text-sm placeholder-white/30"
                            data-testid="ba-message" />
                </div>
                <div className="flex items-center justify-between">
                  <button onClick={back} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm border border-white/10" data-testid="ba-back-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button onClick={validateStep2}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm shadow-[0_8px_30px_rgba(16,185,129,0.35)] hover:-translate-y-0.5 transition-all"
                          data-testid="ba-next-2">
                    Next — Payment <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3 — PAYMENT (Ultra-premium checkout, matches site deposit page) */}
            {step === 3 && (
              <div className="animate-in fade-in duration-200" data-testid="step-payment">
                {/* Tabs: Mobile Wallet vs Crypto */}
                <PaymentChannelSwitcher config={config} selection={selection} onSelect={setSelection} form={form} onFormChange={setForm} coupon={coupon} setCoupon={setCoupon} />

                {/* Sticky checkout footer */}
                <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 via-[#0f1620] to-[#0f1620] p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button onClick={back}
                          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm border border-white/10 w-full sm:w-auto"
                          data-testid="ba-back-3">
                    <ArrowLeft className="w-4 h-4" /> Back to documents
                  </button>

                  <div className="flex items-center justify-between sm:justify-end gap-4">
                    <div className="text-right">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-white/40">You will deposit</div>
                      {coupon.applied ? (
                        <div className="leading-none">
                          <span className="text-sm text-white/40 line-through mr-2">
                            {selection.gateway_method && CRYPTO_METHODS.includes(selection.gateway_method) ? "$100" : "৳13,000"}
                          </span>
                          <span className="text-2xl font-black text-emerald-400">
                            ৳{Number(coupon.applied.final_amount).toLocaleString("en-IN")}
                          </span>
                        </div>
                      ) : (
                        <div className="text-2xl font-black text-emerald-400 leading-none">
                          {selection.gateway_method && CRYPTO_METHODS.includes(selection.gateway_method) ? "$100" : "৳13,000"}
                        </div>
                      )}
                    </div>
                    <button onClick={doSubmit} disabled={submitting || !selection.gateway_id}
                            className="inline-flex items-center gap-2 px-6 lg:px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black text-sm shadow-[0_8px_30px_rgba(16,185,129,0.35)] hover:-translate-y-0.5 transition-all"
                            data-testid="become-agent-submit">
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {submitting ? "Redirecting..." : (
                        <>
                          <Lock className="w-3.5 h-3.5" />
                          Secure Pay
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-white/5 bg-[#0f1620] p-5 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <HeadphonesIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-white">Still have questions?</div>
              <div className="text-xs text-white/50">Admin team is one message away — 24/7 Bangla &amp; English support.</div>
            </div>
          </div>
          <a href={AGENT_TELEGRAM} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#229ED9] hover:bg-[#1E8ABF] text-white text-sm font-bold">
            <Send className="w-4 h-4" /> Message Admin
          </a>
        </div>
      </div>

      <div className="lg:hidden"><MobileBottomBar /></div>
    </div>
  );
}

/* ═══════════════════════════ Sub-components ═══════════════════════════ */

function SectionHeading({ kicker, title, subtitle }) {
  return (
    <div className="mt-10 lg:mt-20 mb-5 lg:mb-8 bap-section-heading">
      <div className="text-[10px] lg:text-[11px] uppercase tracking-widest text-emerald-400 font-bold bap-kicker-in">
        {kicker}
      </div>
      <h2 className="text-xl sm:text-2xl lg:text-4xl font-black text-white mt-1.5 lg:mt-2 tracking-tight leading-tight min-h-[1.25em]">
        <span className="sr-only">{title}</span>
        <span aria-hidden="true">
          <TypeInView text={title} speed={45} startDelay={140} />
        </span>
      </h2>
      {subtitle && (
        <p className="text-xs sm:text-sm lg:text-base text-white/55 mt-1.5 lg:mt-2 max-w-2xl leading-relaxed bap-subtitle-in">
          {subtitle}
        </p>
      )}
      <style>{`
        .bap-kicker-in {
          animation: bap-kicker-kf 640ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
        }
        @keyframes bap-kicker-kf {
          from { opacity: 0; transform: translateX(-10px); letter-spacing: 0.35em; }
          to   { opacity: 1; transform: translateX(0);     letter-spacing: 0.15em; }
        }
        .bap-subtitle-in {
          animation: bap-sub-kf 700ms ease-out 220ms both;
        }
        @keyframes bap-sub-kf {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 0.9; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function HierarchyDiagram() {
  return (
    <div className="w-full flex flex-col items-center">
      <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-amber-500/20 to-amber-500/10 border border-amber-500/40">
        <Crown className="w-5 h-5 text-amber-400" />
        <div className="text-sm font-black text-amber-300">Master Agent</div>
      </div>
      <div className="w-px h-8 bg-gradient-to-b from-amber-500/40 to-emerald-500/40" />
      <div className="grid grid-cols-3 gap-4 lg:gap-10 w-full max-w-2xl">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-px h-6 bg-emerald-500/30" />
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/40">
              <Network className="w-4 h-4 text-emerald-400" />
              <div className="text-xs font-bold text-emerald-300 whitespace-nowrap">Sub Agent</div>
            </div>
            <div className="w-px h-6 bg-white/10" />
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              {[0, 1, 2].map((j) => (
                <div key={j} className="w-8 h-8 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center">
                  <Users className="w-3.5 h-3.5 text-white/50" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RolePerkCard({ icon: Icon, tone, title, tag, perks }) {
  const styles = tone === "amber"
    ? { card: "border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/[0.02]", iconWrap: "bg-amber-500/15 border-amber-500/30 text-amber-400", tag: "bg-amber-500/20 text-amber-300 border-amber-500/30", check: "text-amber-400" }
    : { card: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02]", iconWrap: "bg-emerald-500/15 border-emerald-500/30 text-emerald-400", tag: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", check: "text-emerald-400" };
  return (
    <div className={`rounded-2xl border p-6 ${styles.card}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${styles.iconWrap}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${styles.tag}`}>{tag}</span>
      </div>
      <div className="text-xl font-black text-white">{title}</div>
      <ul className="mt-4 space-y-2.5">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2.5">
            <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${styles.check}`} />
            <span className="text-sm text-white/80 leading-relaxed">{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BonusCell({ icon: Icon, label, value, hint, tone }) {
  const t = tone === "amber"
    ? { border: "border-amber-500/30", bg: "bg-amber-500/10", text: "text-amber-300", num: "text-amber-400" }
    : { border: "border-emerald-500/30", bg: "bg-emerald-500/10", text: "text-emerald-300", num: "text-emerald-400" };
  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} p-4`}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${t.text} font-bold`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-3xl font-black mt-1 ${t.num}`}>{value}</div>
      <div className="text-[11px] text-white/60 mt-1">{hint}</div>
    </div>
  );
}

function BalanceMethodCell({ icon: Icon, label, hint, tone }) {
  const palette = {
    amber:  { border: "border-amber-500/30",  bg: "bg-amber-500/10",  icon: "text-amber-400",  label: "text-amber-300" },
    pink:   { border: "border-pink-500/30",   bg: "bg-pink-500/10",   icon: "text-pink-400",   label: "text-pink-300" },
    orange: { border: "border-orange-500/30", bg: "bg-orange-500/10", icon: "text-orange-400", label: "text-orange-300" },
    purple: { border: "border-purple-500/30", bg: "bg-purple-500/10", icon: "text-purple-400", label: "text-purple-300" },
  }[tone] || { border: "border-white/10", bg: "bg-white/5", icon: "text-white/70", label: "text-white" };
  return (
    <div className={`rounded-xl border ${palette.border} ${palette.bg} p-3.5 flex flex-col gap-1`}>
      <div className={`w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center ${palette.icon}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`text-sm font-bold ${palette.label}`}>{label}</div>
      <div className="text-[10px] text-white/50 leading-tight">{hint}</div>
    </div>
  );
}

function StepPill({ n, title, short, current, icon: Icon }) {
  const active = current === n;
  const done = current > n;
  return (
    <div
      className={`relative rounded-2xl border px-2 sm:px-3 lg:px-4 py-2.5 lg:py-3.5 flex items-center gap-2 sm:gap-2.5 transition-all duration-300 ${
        active
          ? "border-emerald-500/50 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 shadow-[0_8px_30px_-10px_rgba(16,185,129,0.5)]"
          : done
          ? "border-emerald-500/30 bg-emerald-500/[0.04]"
          : "border-white/10 bg-[#0a1119]/70"
      }`}
    >
      <div
        className={`relative w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-black border shrink-0 transition-all ${
          active
            ? "bg-emerald-500 text-black border-emerald-300 shadow-[0_0_0_4px_rgba(16,185,129,0.15)]"
            : done
            ? "bg-emerald-500 text-black border-emerald-400"
            : "bg-white/[0.04] text-white/40 border-white/10"
        }`}
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.15em] ${active ? "text-emerald-300" : done ? "text-emerald-400/70" : "text-white/40"}`}>
          Step {String(n).padStart(2, "0")}
        </div>
        <div className={`text-[11px] sm:text-xs lg:text-sm font-bold truncate mt-0.5 ${active ? "text-white" : done ? "text-white/80" : "text-white/50"}`}>
          <span className="sm:hidden">{short || title}</span>
          <span className="hidden sm:inline">{title}</span>
        </div>
      </div>
    </div>
  );
}

function HeaderTrustPill({ icon: Icon, text }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[10.5px] lg:text-[11px] font-medium text-white/70">
      <Icon className="w-3 h-3 text-emerald-400 shrink-0" />
      <span className="truncate">{text}</span>
    </div>
  );
}

function FormSection({ index, title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-white/[0.07] bg-[#0a1119]/60 p-4 lg:p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="text-[10px] font-black text-emerald-400/80 tracking-[0.2em] mt-0.5">{index}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] lg:text-sm font-bold text-white">{title}</div>
          {subtitle && <div className="text-[11px] text-white/45 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-[11.5px] font-semibold text-white/80">{label}</label>
        {hint && <span className="text-[10px] text-white/35 truncate">{hint}</span>}
      </div>
      <input value={value} onChange={onChange} placeholder={placeholder} type={type}
             className="mt-1.5 w-full rounded-xl bg-[#070d13] border border-white/10 hover:border-white/20 focus:border-emerald-500/60 focus:bg-[#0a1119] focus:ring-2 focus:ring-emerald-500/15 outline-none px-4 py-3 text-sm placeholder-white/25 transition-all"
             data-testid={testId} />
    </div>
  );
}

function UploadTile({ label, value, loading, onFile, onRemove, testId }) {
  const inputRef = useRef(null);
  const pick = () => inputRef.current?.click();
  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
             onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
             data-testid={`${testId}-input`} />
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-emerald-500/40 bg-[#0a1119] h-36 shadow-lg shadow-emerald-500/10">
          <img src={value.preview} alt={label} className="w-full h-full object-cover" />
          <button type="button" onClick={onRemove}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-black flex items-center justify-center border border-white/20"
                  data-testid={`${testId}-remove`}>
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="absolute bottom-0 inset-x-0 px-2 py-1.5 bg-gradient-to-t from-black/80 to-transparent text-[11px] text-white font-medium">
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> {label}</span>
          </div>
        </div>
      ) : (
        <button type="button" onClick={pick} disabled={loading}
                className="w-full h-36 rounded-xl border-2 border-dashed border-white/15 hover:border-emerald-500/50 bg-[#0a1119] hover:bg-[#0d1520] flex flex-col items-center justify-center gap-1.5 text-white/60 hover:text-white transition-all disabled:opacity-60"
                data-testid={`${testId}-btn`}>
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"><Upload className="w-5 h-5" /></div>
          )}
          <div className="text-xs font-semibold text-center px-3 leading-tight mt-1">{label}</div>
          <div className="text-[10px] text-white/40">Tap to upload</div>
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════ Premium Checkout Components ═══════════════════════════ */

const E_WALLET_METHODS = ["bkash", "nagad", "rocket", "upay"];
const CRYPTO_METHODS   = ["usdt_trc20", "usdt_bep20", "trx", "bnb", "eth", "ton", "usdc_polygon"];

/** Brand metadata — only logo + name. Card uses neutral dark bg so real logo shines. */
const BRAND_META = {
  bkash:  { name: "bKash",  sub: "Mobile Banking", accent: "#E2136E", logo: "/payment-logos/bkash.webp" },
  nagad:  { name: "Nagad",  sub: "Mobile Banking", accent: "#EC5B24", logo: "/payment-logos/nagad-real.png" },
  rocket: { name: "Rocket", sub: "Mobile Banking", accent: "#8B2E86", logo: "/payment-logos/rocket.webp" },
  upay:   { name: "Upay",   sub: "Mobile Banking", accent: "#F58120", logo: "/payment-logos/upay.png" },
  usdt_trc20:   { name: "USDT",  sub: "TRC-20",   accent: "#26A17B", logo: "/payment-logos/usdt.svg" },
  usdt_bep20:   { name: "USDT",  sub: "BEP-20",   accent: "#F3BA2F", logo: "/payment-logos/usdt-bep20.png" },
  trx:          { name: "TRX",   sub: "Tron",     accent: "#EF0027", logo: "/payment-logos/trx.svg" },
  bnb:          { name: "BNB",   sub: "BNB Chain",accent: "#F3BA2F", logo: "/payment-logos/bnb.svg" },
  eth:          { name: "ETH",   sub: "Ethereum", accent: "#627EEA", logo: "/payment-logos/eth.svg" },
  ton:          { name: "TON",   sub: "Toncoin",  accent: "#0098EA", logo: "/payment-logos/ton.svg" },
  usdc_polygon: { name: "USDC",  sub: "Polygon",  accent: "#2775CA", logo: "/payment-logos/usdc.svg" },
};

function PaymentChannelSwitcher({ config, selection, onSelect, form, onFormChange, coupon, setCoupon }) {
  const gateways = config?.gateways || [];
  const eWalletGateways = gateways.filter((g) => (g.methods || []).some((m) => E_WALLET_METHODS.includes(m)));
  const cryptoGateways  = gateways.filter((g) => g.provider === "nowpayments" || (g.methods || []).some((m) => CRYPTO_METHODS.includes(m)));

  const initialCat = selection?.gateway_method && CRYPTO_METHODS.includes(selection.gateway_method) ? "crypto" : "ewallet";
  const [tab, setTab] = useState(initialCat);

  const currentMethods = tab === "crypto"
    ? Array.from(new Set(cryptoGateways.flatMap((g) => (g.methods || []).filter((m) => CRYPTO_METHODS.includes(m)))))
    : Array.from(new Set(eWalletGateways.flatMap((g) => (g.methods || []).filter((m) => E_WALLET_METHODS.includes(m)))));

  const availableGateways = selection.gateway_method
    ? gateways.filter((g) => (g.methods || []).includes(selection.gateway_method))
    : [];
  const selectedGateway = availableGateways.find((g) => g.id === selection.gateway_id) || availableGateways[0];
  const displayAmount = tab === "crypto" ? "$100" : "৳13,000";

  const pickMethod = (m) => {
    const gw = gateways.find((g) => (g.methods || []).includes(m));
    onSelect({ gateway_id: gw?.id || "", gateway_method: m });
  };
  const pickGateway = (gwId) => onSelect({ gateway_id: gwId, gateway_method: selection.gateway_method });

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a1119] overflow-hidden">
      {/* Category tabs */}
      <div className="grid grid-cols-2 gap-px bg-white/5 border-b border-white/5">
        <CategoryTab active={tab === "ewallet"} onClick={() => { setTab("ewallet"); onSelect({ gateway_id: "", gateway_method: "" }); }}
                     icon={Smartphone} label="Mobile Wallet" hint="৳13,000 · Auto"
                     count={eWalletGateways.length} testId="pay-tab-ewallet" />
        <CategoryTab active={tab === "crypto"} onClick={() => { setTab("crypto"); onSelect({ gateway_id: "", gateway_method: "" }); }}
                     icon={Bitcoin} label="Cryptocurrency" hint="$100 · Auto"
                     count={cryptoGateways.length} testId="pay-tab-crypto" />
      </div>

      {/* SECTION 01 */}
      <SectionHeader num="01" title="Select deposit channel"
                     subtitle={tab === "crypto" ? "Choose the cryptocurrency you'll pay with" : "Choose the mobile wallet you'll pay with"} />
      <div className="px-5 pb-6">
        {!config ? (
          <div className="py-10 flex items-center justify-center text-white/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : currentMethods.length === 0 ? (
          <div className="py-8 text-center text-sm text-white/50">এই category তে কোনো active gateway নেই।</div>
        ) : (
          <div className={`grid gap-3 ${tab === "crypto" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-3"}`}>
            {currentMethods.map((m) => (
              <BrandMethodCard key={m} method={m} active={selection.gateway_method === m} onClick={() => pickMethod(m)} />
            ))}
          </div>
        )}
      </div>

      {/* SECTION 02 */}
      <SectionHeader num="02" title="Select payment gateway" subtitle="Pick the PSP that will process your payment" />
      <div className="px-5 pb-6">
        {!selection.gateway_method ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-6 text-center text-xs text-white/50">
            আগে channel select করুন
          </div>
        ) : availableGateways.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-6 text-center text-xs text-white/50">
            এই method এর জন্য কোনো active gateway নেই
          </div>
        ) : (
          <div className="space-y-2">
            {availableGateways.map((g) => (
              <GatewayRow key={g.id} gateway={g} selected={selection.gateway_id === g.id} onClick={() => pickGateway(g.id)} />
            ))}
          </div>
        )}

        {selection.gateway_method && E_WALLET_METHODS.includes(selection.gateway_method) && (
          <div className="mt-4">
            <Field label={`আপনার ${(BRAND_META[selection.gateway_method]?.name || selection.gateway_method)} নাম্বার (Optional)`}
                   value={form.payer_account}
                   onChange={(e) => onFormChange((f) => ({ ...f, payer_account: e.target.value }))}
                   testId="ba-payer-account" placeholder="01XXXXXXXXX" />
          </div>
        )}
      </div>

      {/* SECTION 03 — LOCKED amount */}
      <SectionHeader num="03" title="Deposit amount" subtitle="Fixed initial Agent Balance — apply coupon if you have one" />
      <div className="px-5 pb-5">
        <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] px-4 lg:px-5 py-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              {coupon?.applied ? (
                <>
                  <span className="text-3xl lg:text-4xl font-black text-emerald-400 leading-none">
                    ৳{Number(coupon.applied.final_amount).toLocaleString("en-IN")}
                  </span>
                  <span className="text-lg text-white/40 line-through">{displayAmount}</span>
                </>
              ) : (
                <span className="text-3xl lg:text-4xl font-black text-emerald-400 leading-none">{displayAmount}</span>
              )}
              <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300">AUTO</span>
              {coupon?.applied && (
                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-300">
                  -{coupon.applied.discount_type === "percent" ? `${coupon.applied.discount_value}%` : `৳${coupon.applied.discount}`}
                </span>
              )}
            </div>
            <div className="text-[11px] text-white/50 mt-1.5">
              {coupon?.applied
                ? `Coupon ${coupon.applied.code} applied — you save ৳${Number(coupon.applied.discount).toLocaleString("en-IN")}`
                : "Fixed for agent applications · Credited to your Agent Balance"}
            </div>
          </div>
        </div>

        {/* Coupon input */}
        <CouponInput coupon={coupon} setCoupon={setCoupon} />

        <div className="grid grid-cols-3 gap-2 mt-4">
          <TrustChip icon={Shield} label="PSP encrypted" />
          <TrustChip icon={Zap} label="Instant credit" />
          <TrustChip icon={CheckCircle2} label="Auto-confirm" />
        </div>

        <div className="mt-4 rounded-xl border border-white/5 bg-white/[0.02] p-4 text-xs text-white/60 leading-relaxed">
          Pick a gateway, choose {tab === "crypto" ? "your cryptocurrency" : "bKash / Nagad / Rocket"}, and Submit —
          আপনাকে gateway এর secure checkout এ redirect করা হবে। Payment confirm হওয়ার সাথে সাথে
          ৳13,000 Agent Balance এ credit হবে এবং PDF receipt সহ email আসবে{" "}
          <span className="text-emerald-300 font-semibold">{form?.email || "your address"}</span> এ।
        </div>

        {selectedGateway && (
          <div className="mt-4 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-3 flex items-center gap-2 text-sm">
            <ArrowRight className="w-4 h-4 text-emerald-400" />
            <span className="text-white/70">You will pay via</span>
            <span className="font-bold text-emerald-300">{selectedGateway.label}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ num, title, subtitle }) {
  return (
    <div className="px-5 pt-5 pb-3 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-xs font-black shrink-0">
        {num}
      </div>
      <div className="min-w-0">
        <div className="text-base lg:text-lg font-bold text-white leading-tight">{title}</div>
        <div className="text-xs text-white/50 mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function CategoryTab({ active, onClick, icon: Icon, label, hint, count, testId }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-4 py-4 flex items-center gap-3 text-left transition-colors ${
        active ? "bg-emerald-500/10 border-b-2 border-emerald-400" : "bg-[#0a1119] hover:bg-white/[0.02] border-b-2 border-transparent"
      }`}
      data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center border shrink-0 ${
        active ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-white/60"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`text-sm font-bold ${active ? "text-white" : "text-white/80"} truncate`}>{label}</div>
          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${active ? "bg-emerald-500 text-black" : "bg-white/10 text-white/60"}`}>{count}</span>
        </div>
        <div className={`text-[10px] mt-0.5 truncate ${active ? "text-emerald-300/80" : "text-white/40"}`}>{hint}</div>
      </div>
    </button>
  );
}

function BrandMethodCard({ method, active, onClick }) {
  const meta = BRAND_META[method] || { name: method.toUpperCase(), sub: "", accent: "#10b981", logo: "" };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-xl border-2 transition-all group bg-[#0a1119] py-3 px-2 flex flex-col items-center justify-center gap-1.5 ${
        active
          ? "border-emerald-400 shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_6px_20px_rgba(16,185,129,0.2)]"
          : "border-white/10 hover:border-white/25 hover:-translate-y-0.5"
      }`}
      data-testid={`method-${method}`}
    >
      {active && (
        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 text-black flex items-center justify-center border border-[#0a1119] z-10">
          <CheckCircle2 className="w-2.5 h-2.5" />
        </div>
      )}
      <div className="absolute top-0 inset-x-0 h-0.5 rounded-t-xl" style={{ background: meta.accent, opacity: 0.75 }} />

      {/* Logo badge — uniform 24px white circle. Works for every brand PNG (transparent OR with own bg baked in). */}
      <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm">
        {meta.logo ? (
          <img src={meta.logo} alt={meta.name} className="w-5 h-5 object-contain" draggable={false} />
        ) : (
          <span className="font-black text-[10px]" style={{ color: meta.accent }}>{meta.name[0]}</span>
        )}
      </div>
      <div className="text-white font-bold text-xs leading-tight">{meta.name}</div>
      {meta.sub && <div className="text-white/45 text-[9px] leading-tight">{meta.sub}</div>}
    </button>
  );
}

function GatewayRow({ gateway, selected, onClick }) {
  const code = (gateway.provider || "?").slice(0, 2).toUpperCase();
  return (
    <button type="button" onClick={onClick}
      className={`w-full rounded-xl border transition-all flex items-center gap-3 px-3 py-3 text-left ${
        selected ? "border-emerald-500/50 bg-emerald-500/[0.06] shadow-[0_0_0_1px_rgba(16,185,129,0.2)]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
      }`}
      data-testid={`gateway-${gateway.id}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-black text-xs shrink-0 ${
        selected ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300" : "bg-white/5 border border-white/10 text-white/60"
      }`}>
        {code}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-white truncate">{gateway.label}</div>
          {gateway.recommended && (
            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
              <Sparkles className="w-2.5 h-2.5" /> Recommended
            </span>
          )}
        </div>
        <div className="text-[11px] text-white/45 truncate mt-0.5">
          Bangladesh PSP · Tier 1 · supports {(gateway.methods || []).slice(0, 3).join(" / ")}
        </div>
      </div>
      {selected ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : <div className="w-5 h-5 rounded-full border border-white/20 shrink-0" />}
    </button>
  );
}

function TrustChip({ icon: Icon, label }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 flex items-center gap-2">
      <div className="w-6 h-6 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
        <Icon className="w-3 h-3" />
      </div>
      <span className="text-[11px] font-semibold text-white/80 truncate">{label}</span>
    </div>
  );
}

function CouponInput({ coupon, setCoupon }) {
  const [input, setInput] = useState(coupon?.code || "");
  const [err, setErr] = useState("");

  const apply = async () => {
    const code = (input || "").trim().toUpperCase();
    if (!code) { setErr("Enter a coupon code"); return; }
    setErr("");
    setCoupon((c) => ({ ...c, applying: true }));
    try {
      const { data } = await axios.post(`${BACKEND}/api/site/agent-apply/validate-coupon`, { code });
      setCoupon({ code, applying: false, applied: data });
      toast.success(`Coupon applied — you save ৳${Number(data.discount).toLocaleString("en-IN")}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid coupon";
      setErr(typeof msg === "string" ? msg : "Invalid coupon");
      setCoupon((c) => ({ ...c, applying: false, applied: null }));
    }
  };

  const remove = () => {
    setInput("");
    setErr("");
    setCoupon({ code: "", applying: false, applied: null });
  };

  if (coupon?.applied) {
    return (
      <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 shrink-0">
          <Ticket className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-black text-white">{coupon.applied.code}</div>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-black">
              APPLIED
            </span>
          </div>
          <div className="text-[11px] text-white/60 truncate mt-0.5">
            {coupon.applied.label || (coupon.applied.discount_type === "percent"
              ? `${coupon.applied.discount_value}% off`
              : `৳${coupon.applied.discount_value} off`)} · you save ৳{Number(coupon.applied.discount).toLocaleString("en-IN")}
          </div>
        </div>
        <button
          type="button"
          onClick={remove}
          className="text-xs text-white/50 hover:text-red-400 px-2 py-1 rounded"
          data-testid="ba-coupon-remove"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 lg:p-3.5 flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 shrink-0">
          <Tag className="w-4 h-4" />
        </div>
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value.toUpperCase()); setErr(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
          placeholder="Have a coupon code? (Optional)"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder-white/30 uppercase tracking-wider"
          data-testid="ba-coupon-input"
        />
        <button
          type="button"
          onClick={apply}
          disabled={coupon?.applying || !input.trim()}
          className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black text-xs font-black transition-colors shrink-0"
          data-testid="ba-coupon-apply"
        >
          {coupon?.applying && <Loader2 className="w-3 h-3 animate-spin" />}
          Apply
        </button>
      </div>
      {err && (
        <div className="mt-1.5 text-[11px] text-red-400 flex items-center gap-1 pl-1">
          <X className="w-3 h-3" /> {err}
        </div>
      )}
    </div>
  );
}

function PolicyCard({ icon: Icon, title, body, tone }) {
  const t = tone === "red"
    ? { border: "border-red-500/30", bg: "bg-gradient-to-br from-red-500/10 to-red-500/[0.02]", iconBg: "bg-red-500/15 border-red-500/30 text-red-400", title: "text-red-300" }
    : { border: "border-amber-500/30", bg: "bg-gradient-to-br from-amber-500/10 to-amber-500/[0.02]", iconBg: "bg-amber-500/15 border-amber-500/30 text-amber-400", title: "text-amber-300" };
  return (
    <div className={`rounded-2xl border ${t.border} ${t.bg} p-3.5 sm:p-4 lg:p-5 flex flex-col sm:flex-row gap-2 sm:gap-3`}>
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl border flex items-center justify-center shrink-0 ${t.iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className={`text-[13px] sm:text-sm lg:text-base font-black leading-snug ${t.title}`}>{title}</div>
        <div className="text-[11px] sm:text-xs lg:text-sm text-white/70 mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
